import React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  HStack,
  Icon,
  useDisclosure,
} from "@chakra-ui/react";
import {
  FiAlertTriangle,
  FiTrash2,
  FiInfo,
  FiCheckCircle,
} from "react-icons/fi";

const ConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title = "Confirm Action",
  message = "Are you sure you want to proceed?",
  confirmText = "Confirm",
  cancelText = "Cancel",
  type = "default", // default, delete, warning, info, success
  size = "md",
  isLoading = false,
  children,
  ...props
}) => {
  // Get icon and colors based on type
  const getTypeConfig = () => {
    switch (type) {
      case "delete":
        return {
          icon: FiTrash2,
          iconColor: "red.500",
          confirmColorScheme: "red",
          title: title || "Delete Item",
          message:
            message ||
            "Are you sure you want to delete this item? This action cannot be undone.",
        };
      case "warning":
        return {
          icon: FiAlertTriangle,
          iconColor: "orange.500",
          confirmColorScheme: "orange",
          title: title || "Warning",
          message:
            message ||
            "This action may have consequences. Do you want to continue?",
        };
      case "info":
        return {
          icon: FiInfo,
          iconColor: "blue.500",
          confirmColorScheme: "blue",
          title: title || "Information",
          message: message || "Please confirm to proceed.",
        };
      case "success":
        return {
          icon: FiCheckCircle,
          iconColor: "green.500",
          confirmColorScheme: "green",
          title: title || "Success",
          message: message || "Action completed successfully.",
        };
      default:
        return {
          icon: FiInfo,
          iconColor: "gray.500",
          confirmColorScheme: "blue",
          title,
          message,
        };
    }
  };

  const config = getTypeConfig();

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
  };

  const handleCancel = () => {
    if (onClose) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={size}
      isCentered
      closeOnOverlayClick={!isLoading}
      closeOnEsc={!isLoading}
      {...props}
    >
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={3} align="center">
            <Icon as={config.icon} color={config.iconColor} boxSize={5} />
            <Text fontSize="lg" fontWeight="semibold">
              {config.title}
            </Text>
          </HStack>
        </ModalHeader>

        {!isLoading && <ModalCloseButton />}

        <ModalBody pb={6}>
          {children ? (
            children
          ) : (
            <Text color="gray.600" lineHeight="tall">
              {config.message}
            </Text>
          )}
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3} w="full" justify="flex-end">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={isLoading}
            >
              {cancelText}
            </Button>
            <Button
              colorScheme={config.confirmColorScheme}
              onClick={handleConfirm}
              isLoading={isLoading}
              loadingText="Processing..."
              disabled={isLoading}
            >
              {confirmText}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ConfirmDialog;
